import React, { useEffect, useMemo, useRef, useState } from "react";
import { allZones, clock, dayParts, groupByDay, pad2, zoneLabel } from "./format";

/*
 * The pieces every booking screen is built from, drawn to Claude Design's
 * concept D v4 (Remotion_Booking_-_The_Agenda_v4.pptx): the dark header, the
 * day strip and time grid, the yellow selection with its hard shadow, and the
 * numbered agenda lines.
 */

/*
 * The tab a lead sees says who they are booking with, not "Remotion CRM".
 *
 * On desktop the page widens (`bk--wide`), and a screen with no big header of
 * its own (the states, cancel) gets the thin black bar with the logo, which is
 * what `bar` names the right-hand side of. Kept here rather than imported from
 * desktop.js, which imports this file.
 */
function usePageWide() {
  const query = "(min-width: 1000px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setWide(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return wide;
}

export function Page({ children, title = "Book a call with Gabriel · Remotion", bar = null }) {
  const wide = usePageWide();
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
  return (
    <div className={`bk${wide ? " bk--wide" : ""}`}>
      {wide && bar ? (
        <div className="bkd-bar">
          <div className="bkd-bar__left">
            <img
              className="bkd-logo"
              src="/remotion-logo.png"
              srcSet="/remotion-logo.png 1x, /remotion-logo@2x.png 2x"
              alt="Remotion"
            />
            {typeof bar === "object" && bar.left ? bar.left : null}
          </div>
          <span className="bk-mono bkd-bar__right">{typeof bar === "object" ? bar.right : bar}</span>
        </div>
      ) : null}
      {children}
    </div>
  );
}

/*
 * The strip across the top of a preview, so a screenshot of one can never be
 * mistaken for the live page.
 */
export function PreviewBar() {
  return <div className="bk-previewbar">Preview · nothing here books a call</div>;
}

/** The dark band. `kicker` is the mono line above the headline. */
export function Header({ kicker, right, logo = false, children, tone = "dark" }) {
  return (
    <header className={`bk-header bk-header--${tone}`}>
      <div className="bk-header__row">
        {logo ? <img
            className="bk-logo"
            src="/remotion-logo.png"
            srcSet="/remotion-logo.png 1x, /remotion-logo@2x.png 2x"
            alt="Remotion"
          /> : <span className="bk-mono bk-kicker">{kicker}</span>}
        {right ? <span className="bk-mono bk-header__right">{right}</span> : null}
      </div>
      {children ? <h1 className="bk-headline">{children}</h1> : null}
    </header>
  );
}

export function Mono({ children, className = "" }) {
  return <span className={`bk-mono ${className}`}>{children}</span>;
}

/** "London (GMT+1) · Change", where Change is a native select: best on a phone. */
export function ZonePicker({ zone, onChange }) {
  const zones = useMemo(() => {
    const list = allZones();
    return list.includes(zone) ? list : [zone, ...list];
  }, [zone]);
  return (
    <label className="bk-zone">
      <span className="bk-zone__text">
        {zoneLabel(zone)} · Change
      </span>
      <select aria-label="Your timezone" value={zone} onChange={(e) => onChange(e.target.value)}>
        {zones.map((z) => (
          <option key={z} value={z}>
            {z.replace(/_/g, " ")}
          </option>
        ))}
      </select>
    </label>
  );
}

/** The day strip and the time grid under it. */
export function SlotPicker({ slots, zone, selected, onSelect, day, onDay, disabledStart = null }) {
  const days = useMemo(() => groupByDay(slots, zone), [slots, zone]);
  const keys = [...days.keys()];
  const activeDay = day && days.has(day) ? day : keys[0];
  const strip = useRef(null);

  useEffect(() => {
    const el = strip.current && strip.current.querySelector("[aria-pressed='true']");
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeDay]);

  return (
    <div className="bk-picker">
      <div className="bk-days" ref={strip} role="group" aria-label="Day">
        {keys.map((key) => {
          const p = dayParts(key);
          return (
            <button
              key={key}
              type="button"
              className="bk-day"
              aria-pressed={key === activeDay}
              onClick={() => onDay(key)}
            >
              <span className="bk-day__wd">{p.weekday}</span>
              <span className="bk-day__d">{p.day}</span>
              <span className="bk-day__m">{p.month}</span>
            </button>
          );
        })}
      </div>
      <TimeGrid
        slots={days.get(activeDay) || []}
        zone={zone}
        selected={selected}
        onSelect={onSelect}
        disabledStart={disabledStart}
      />
    </div>
  );
}

export function TimeGrid({ slots, zone, selected, onSelect, disabledStart = null }) {
  return (
    <div className="bk-times" role="group" aria-label="Time">
      {slots.map((slot) => {
        const taken = disabledStart && slot.start === disabledStart;
        return (
          <button
            key={slot.start}
            type="button"
            className="bk-time"
            disabled={taken}
            aria-pressed={selected === slot.start}
            onClick={() => onSelect(slot)}
          >
            {clock(slot.start, zone)}
          </button>
        );
      })}
    </div>
  );
}

export function PrimaryButton({ children, busy, ...props }) {
  return (
    <button type="button" className="bk-primary" disabled={busy || props.disabled} {...props}>
      {busy ? "One moment…" : children}
    </button>
  );
}

export function SecondaryButton({ children, ...props }) {
  return (
    <button type="button" className="bk-secondary" {...props}>
      {children}
    </button>
  );
}

export function TextLink({ children, ...props }) {
  return (
    <button type="button" className="bk-link" {...props}>
      {children}
    </button>
  );
}

/** The numbered agenda: "00  What you've tried and what happened". */
export function AgendaLines({ agenda, compact = false }) {
  if (!agenda) return null;
  if (compact) {
    return (
      <p className="bk-agenda-compact">
        {agenda.items.map((item, i) => (
          <React.Fragment key={item.at}>
            {i ? " · " : ""}
            <span className="bk-at">{pad2(item.at)}</span> {item.title}
          </React.Fragment>
        ))}
      </p>
    );
  }
  return (
    <ol className="bk-agenda">
      {agenda.items.map((item) => (
        <li key={item.at}>
          <span className="bk-at">{pad2(item.at)}</span>
          <span>{item.title}</span>
        </li>
      ))}
    </ol>
  );
}

/** The three "What's this call for?" cards. */
export function AgendaChoices({ agendas, value, onChange }) {
  return (
    <div className="bk-choices" role="radiogroup" aria-label="What's this call for?">
      {agendas.map((agenda) => (
        <button
          key={agenda.key}
          type="button"
          role="radio"
          aria-checked={value === agenda.key}
          className="bk-choice"
          onClick={() => onChange(value === agenda.key ? value : agenda.key)}
        >
          {agenda.label}
        </button>
      ))}
    </div>
  );
}

/*
 * "Anything you want to add or ask?" Saves on its own: after a pause in typing
 * and on blur, never on every keystroke, and says so quietly.
 */
export function NoteBox({ value, onSave, placeholder, autoFocus = false }) {
  const [text, setText] = useState(value || "");
  const [state, setState] = useState(value ? "saved" : "idle");
  const lastSaved = useRef(value || "");
  const timer = useRef(null);

  const flush = async (next) => {
    clearTimeout(timer.current);
    if (next.trim() === lastSaved.current.trim()) return;
    setState("saving");
    try {
      await onSave(next);
      lastSaved.current = next;
      setState(next.trim() ? "saved" : "idle");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="bk-note">
      <textarea
        className={`bk-textarea ${text ? "" : "bk-textarea--empty"}`}
        rows={3}
        maxLength={2000}
        value={text}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          setState("typing");
          clearTimeout(timer.current);
          timer.current = setTimeout(() => flush(next), 900);
        }}
        onBlur={() => flush(text)}
      />
      <p className={`bk-saved bk-saved--${state}`} aria-live="polite">
        {state === "saved" ? "✓ Added to the agenda" : state === "error" ? "Couldn't save that. Try again in a moment." : " "}
      </p>
    </div>
  );
}

/*
 * "Anyone else joining?" — a colleague onto the Google invite, after booking
 * (asked 2026-10-07). One email field, because the booking itself stayed three
 * fields; Google sends them the invite and the Meet link. Only guests added
 * here are listed, and only they can be taken off here.
 */
export function GuestBox({ guests = [], max = 5, onAdd, onRemove, stacked = false }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { ok, text }

  const add = async (e) => {
    e.preventDefault();
    const next = email.trim();
    if (!next || busy) return;
    setBusy(true);
    setStatus(null);
    try {
      await onAdd(next);
      setEmail("");
      setStatus({ ok: true, text: `✓ Google sent ${next.toLowerCase()} the invite` });
    } catch (err) {
      setStatus({ ok: false, text: err.message || "Couldn't add them. Try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (guest) => {
    setStatus(null);
    try {
      await onRemove(guest.email);
    } catch (err) {
      setStatus({ ok: false, text: err.message || "Couldn't take them off. Try again in a moment." });
    }
  };

  return (
    <div className="bk-guests">
      {guests.length ? (
        <ul className="bk-guests__list">
          {guests.map((g) => (
            <li key={g.email}>
              <span>{g.name ? `${g.name} · ${g.email}` : g.email}</span>
              <TextLink onClick={() => remove(g)} aria-label={`Take ${g.email} off the call`}>
                Remove
              </TextLink>
            </li>
          ))}
        </ul>
      ) : null}
      {guests.length < max ? (
        <form className={`bk-guests__form${stacked ? " bk-guests__form--stacked" : ""}`} onSubmit={add}>
          <input
            className="bk-input"
            type="email"
            inputMode="email"
            autoComplete="off"
            maxLength={254}
            placeholder="name@company.com"
            aria-label="Colleague's email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button type="submit" className="bk-secondary bk-guests__add" disabled={busy || !email.trim()}>
            {busy ? "Adding…" : "Add"}
          </button>
        </form>
      ) : null}
      <p className={`bk-saved${status && !status.ok ? " bk-saved--error" : ""}`} aria-live="polite">
        {status ? status.text : " "}
      </p>
    </div>
  );
}

export function Field(props) {
  return <input className="bk-input" {...props} />;
}

export function Divider() {
  return <hr className="bk-rule" />;
}

export function Skeleton() {
  return (
    <div className="bk-skeleton" aria-label="Finding open times">
      <div className="bk-skeleton__row">
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} className="bk-skeleton__day" />
        ))}
      </div>
      <div className="bk-skeleton__grid">
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} className="bk-skeleton__time" />
        ))}
      </div>
    </div>
  );
}
